/**
 * Plain-language copy for the default Task experience.
 * Technical identifiers stay in advanced details, not in these strings.
 */

export const PROHIBITED_DEFAULT_TERMS = [
  "intent contract",
  "feature plan",
  "promise draft",
  "orchestration",
  "fingerprint",
  "human_accept",
  "dual-lens",
  "criterion disposition",
  "lifecycle event",
  "reconciliation",
  "analysis source",
  "processor version",
];

export function findProhibitedDefaultTerms(text) {
  const value = String(text || "").toLowerCase();
  return PROHIBITED_DEFAULT_TERMS.filter((term) => value.includes(term));
}

export const RECOMMENDATION_COPY = {
  approve: "Looks ready to approve",
  approve_conditionally: "More proof needed before approval",
  do_not_approve: "Changes required",
  rerun: "Review latest version",
  already_decided: "A decision is already recorded",
};

const BLOCKER_RULES = [
  {
    test: /stale|changed since|no longer describe|branch_changed|out of date/i,
    text: "The delivered code changed after the last review.",
    owner: "Reviewer",
    outside: false,
  },
  {
    test: /\bci\b|test run|passing test|exact commit|exact head|workflow|ci_not_passing/i,
    text: "The latest code has not passed its required tests.",
    owner: "Administrator",
    outside: true,
  },
  {
    test: /independent|separation|same person|created this review|decider/i,
    text: "Someone other than the person who created this review must make the final decision.",
    owner: "Independent reviewer",
    outside: false,
  },
  {
    test: /unrelated|not applicable|pii|retention|sensitive data/i,
    text: "Some listed requirements may not belong to this Task.",
    owner: "Product",
    outside: false,
  },
];

export function plainBlocker(raw = {}) {
  const source = `${raw.reasonCode || raw.code || ""} ${raw.text || raw.detail || raw.reason || ""}`;
  const match = BLOCKER_RULES.find((rule) => rule.test.test(source));
  if (match) {
    return {
      reasonCode: raw.reasonCode || raw.code || null,
      text: match.text,
      owner: match.owner,
      outsideRenderRepo: match.outside,
    };
  }
  const text = String(raw.text || raw.detail || raw.reason || "").trim();
  if (!text) return null;
  return {
    reasonCode: raw.reasonCode || raw.code || null,
    text: text.replace(/\bSHA\b/g, "version").replace(/commit/gi, "version"),
    owner: raw.owner || raw.ownerLabel || null,
    outsideRenderRepo: Boolean(raw.outsideRenderRepo),
  };
}

export function uniquePlainBlockers(items = []) {
  const seen = new Set();
  const out = [];
  items.forEach((item) => {
    const next = plainBlocker(item);
    if (!next) return;
    if (seen.has(next.text)) return;
    seen.add(next.text);
    out.push(next);
  });
  return out;
}

export function customerStatusCopy(stage, status) {
  if (stage === "Waiting for decision" && status === "Blocked") {
    return {
      statusLabel: "Waiting for test results",
      statusExplanation:
        "The work was reviewed, but it cannot be approved until the latest version passes its required tests.",
      whatHappensNext:
        "An administrator connects the test result. Then someone else can make the decision.",
      primaryAction: { id: "view_review", label: "View review", section: "review" },
      owner: { role: "administrator", label: "Administrator" },
    };
  }
  if (stage === "Waiting for decision") {
    return {
      statusLabel: "Waiting for a decision",
      statusExplanation: "The review is ready. A reviewer can now decide.",
      whatHappensNext: "A reviewer approves, approves with conditions, or asks for changes.",
      primaryAction: { id: "record_decision", label: "Make decision", section: "review" },
      owner: { role: "reviewer", label: "Reviewer" },
    };
  }
  if (stage === "Ready for review") {
    return {
      statusLabel: "Ready for review",
      statusExplanation: "Engineering accepted this Task. Paste the pull request or branch that contains the work.",
      whatHappensNext: "Connect the delivered work, then RenderRepo can review it.",
      primaryAction: { id: "connect_delivered_work", label: "Connect delivered work", section: "delivery" },
      owner: { role: "reviewer", label: "Reviewer" },
    };
  }
  if (stage === "Waiting for engineering") {
    return {
      statusLabel: "Waiting for engineering",
      statusExplanation: "Product sent this Task. Engineering still needs to accept it.",
      whatHappensNext: "An engineer accepts the Task or asks for changes.",
      primaryAction: { id: "acknowledge", label: "Acknowledge Task", section: "engineering" },
      owner: { role: "engineering", label: "Engineering" },
    };
  }
  if (stage === "Ready for handoff") {
    return {
      statusLabel: "Ready to send",
      statusExplanation: "The plan is ready for product to send to engineering.",
      whatHappensNext: "Product confirms the Task, then engineering can accept it.",
      primaryAction: { id: "approve_task", label: "Review Task", section: "task" },
      owner: { role: "product", label: "Product" },
    };
  }
  if (stage === "Planning") {
    return {
      statusLabel: "Planning",
      statusExplanation: "This Task still needs a few product answers.",
      whatHappensNext: "Answer the current question so the plan can be finished.",
      primaryAction: { id: "answer_question", label: "Continue talking", section: "conversation" },
      owner: { role: "product", label: "Product" },
    };
  }
  if (stage === "Completed") {
    return {
      statusLabel: "Complete",
      statusExplanation: "A reviewer recorded a decision on this work.",
      whatHappensNext: "Nothing is waiting on this Task.",
      primaryAction: null,
      owner: { role: "none", label: "No one" },
    };
  }
  if (stage === "Needs attention") {
    return {
      statusLabel: status === "Stale" ? "The latest work needs review" : "Needs attention",
      statusExplanation:
        status === "Stale"
          ? "The work changed after the last review. The previous review no longer describes the latest version."
          : "This Task needs attention before it can move forward.",
      whatHappensNext:
        status === "Stale"
          ? "Review the latest version. Then someone else can make the decision."
          : "Open the review to see what to fix.",
      primaryAction: {
        id: status === "Stale" ? "review_latest_version" : "view_review",
        label: status === "Stale" ? "Review latest version" : "View review",
        section: status === "Stale" ? "delivery" : "review",
      },
      owner: { role: "reviewer", label: "a reviewer" },
    };
  }
  return {
    statusLabel: stage || "Working",
    statusExplanation: "This Task is in progress.",
    whatHappensNext: "Continue from the current step.",
    primaryAction: { id: "continue", label: "Continue", section: "task" },
    owner: { role: "product", label: "Product" },
  };
}

export function customerReviewSummary(review, blockers = []) {
  const server = review?.founderPresentation;
  if (server && typeof server === "object" && Array.isArray(server.proven)) {
    return {
      recommendation: server.recommendation,
      why: server.why,
      looksGood: server.proven.slice(0, 5),
      needsAttention: (server.needsAttention || []).slice(0, 5),
      couldNotCheck: (server.couldNotCheck || []).slice(0, 5),
      next: server.next,
      score: {
        supported: review?.aggregate?.met ?? 0,
        partial: review?.aggregate?.partiallyMet ?? 0,
        unverifiable: review?.aggregate?.cannotVerify ?? 0,
        unsupported: review?.aggregate?.notMet ?? 0,
      },
      assessmentLabel: server.assessmentLabel || "AI assessment",
      humanDecisionLabel: server.humanDecisionLabel || "No human decision yet",
      canDecide: Boolean(server.canDecide),
    };
  }

  const handover = review?.handover || {};
  const key = handover.recommendation || "";
  const stale = Boolean(review?.staleState && review.staleState !== "fresh");
  const humanVerdict = review?.reviewVerdict || "not_evaluated";
  const results = Array.isArray(review?.criteriaEvaluation?.results)
    ? review.criteriaEvaluation.results
    : [];

  const proven = [];
  const needsAttention = [];
  const couldNotCheck = [];

  for (const result of results) {
    const label = String(result.displayText || result.criterionText || "Requirement").trim();
    if (!label) continue;
    if (result.status === "met" || result.status === "partially_met") {
      if (proven.length < 5) proven.push(label);
    } else if (result.status === "cannot_verify") {
      if (couldNotCheck.length < 5) couldNotCheck.push(label);
    } else if (result.status === "not_met" || result.status === "contradicted") {
      if (needsAttention.length < 5) needsAttention.push(label);
    }
  }

  const plain = uniquePlainBlockers([
    ...(Array.isArray(handover.blockers) ? handover.blockers.map((text) => ({ detail: text })) : []),
    ...blockers,
  ]);
  for (const item of plain) {
    if (needsAttention.length >= 5) break;
    if (!needsAttention.includes(item.text)) needsAttention.push(item.text);
  }

  for (const risk of Array.isArray(handover.residualRisks) ? handover.residualRisks : []) {
    if (couldNotCheck.length >= 5) break;
    const text = String(risk || "").trim();
    if (text && !couldNotCheck.includes(text)) couldNotCheck.push(text);
  }

  if (proven.length === 0 && (review?.aggregate?.met || 0) > 0) {
    proven.push(`${review.aggregate.met} requirement(s) look supported by the reviewed change.`);
  }
  if (proven.length === 0 && needsAttention.length === 0 && couldNotCheck.length === 0) {
    proven.push("A review of the delivered work is on file.");
  }

  const why =
    (typeof handover.summary === "string" && handover.summary.trim()) ||
    (stale
      ? "The work changed after this review. Review the current version before anyone decides."
      : plain[0]?.text ||
        (typeof handover.headline === "string" && handover.headline.trim()) ||
        "Open the details below to inspect what was checked.");

  const nextFromChecks = Array.isArray(handover.requiredHumanChecks)
    ? handover.requiredHumanChecks.find((item) => String(item || "").trim())
    : null;
  let next =
    stale
      ? "Review the current version, then record a human decision."
      : nextFromChecks ||
        (key === "do_not_approve"
          ? "Request changes or fix the issues, then run a new review."
          : key === "approve"
            ? "An independent reviewer can accept this work."
            : key === "approve_conditionally"
              ? "Accept only with owned follow-ups, or request changes."
              : "Decide what to do next using the single primary action below.");

  if (humanVerdict && humanVerdict !== "not_evaluated") {
    next =
      humanVerdict === "conditionally_accepted"
        ? "Follow up on open conditions, or treat this acceptance as incomplete until they are done."
        : "A human decision is already recorded for this review.";
  }

  return {
    recommendation:
      RECOMMENDATION_COPY[key] ||
      (stale ? "Review latest version" : "More proof needed before approval"),
    why,
    looksGood: proven.slice(0, 5),
    needsAttention: needsAttention.slice(0, 5),
    couldNotCheck: couldNotCheck.slice(0, 5),
    next,
    score: {
      supported: review?.aggregate?.met ?? 0,
      partial: review?.aggregate?.partiallyMet ?? 0,
      unverifiable: review?.aggregate?.cannotVerify ?? 0,
      unsupported: review?.aggregate?.notMet ?? 0,
    },
    assessmentLabel: "AI assessment",
    humanDecisionLabel:
      humanVerdict === "not_evaluated"
        ? "No human decision yet"
        : humanVerdict === "accepted"
          ? "Accepted by a human"
          : humanVerdict === "conditionally_accepted"
            ? "Conditionally accepted by a human"
            : humanVerdict === "rejected"
              ? "Rejected by a human"
              : "Human decision recorded",
    canDecide: humanVerdict === "not_evaluated" && !stale,
  };
}

export function conciseTaskSummary(requestText = "") {
  const text = String(requestText || "").replace(/\s+/g, " ").trim();
  if (!text) return "This Task does not have a written outcome yet.";
  if (text.length <= 280) return text;
  return `${text.slice(0, 277).trim()}…`;
}

export function progressCopy(stage) {
  const afterPlan = [
    "Ready for handoff",
    "Waiting for engineering",
    "In progress",
    "Ready for review",
    "Waiting for decision",
    "Completed",
  ].includes(stage);
  return [
    { id: "plan", label: "Plan agreed", done: afterPlan },
    { id: "handoff", label: "Engineering accepted", done: ["In progress", "Ready for review", "Waiting for decision", "Completed"].includes(stage) },
    { id: "review", label: "Work reviewed", done: ["Waiting for decision", "Completed"].includes(stage) },
    { id: "decision", label: "Final decision waiting", done: stage === "Completed", waiting: stage === "Waiting for decision" },
  ].map((item) => {
    if (item.done && item.id !== "decision") return { ...item, stateLabel: "Done" };
    if (item.waiting) return { ...item, stateLabel: "Waiting" };
    if (item.id === "decision" && item.done) return { ...item, stateLabel: "Done" };
    return { ...item, stateLabel: "Not yet" };
  });
}
